import React from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import Footer from '../../Footer/Footer';
import WhatsOn from '../../common/What-on';
import { getCanonicalUrl } from '../../../utils/seo';

const HERO_IMG =
  'https://www.belvoircastle.com/wp/wp-content/uploads/2025/11/reindeer.webp';
const GALLERY = [
  {
    src: HERO_IMG,
    alt: "A close-up of one of Santa's reindeer at Belvoir Retail Village",
  },
  {
    src: 'https://BelvoirCastle.b-cdn.net/Christmas/reindeer/WhatsApp%20Image%202026-10-06%20at%2013.07.05%20(1).webp',
    alt: 'A handler with two reindeer in the pen at Belvoir Retail Village',
  },
  {
    src: 'https://BelvoirCastle.b-cdn.net/Christmas/reindeer/WhatsApp%20Image%202026-10-06%20at%2013.07.05%20(2).webp',
    alt: 'Visitors meeting the reindeer at Belvoir Retail Village',
  },
  {
    src: 'https://BelvoirCastle.b-cdn.net/Christmas/reindeer/WhatsApp%20Image%202026-10-06%20at%2013.07.05.webp',
    alt: 'Two reindeer with a handler at Belvoir Retail Village',
  },
];

const OTHER_RETAIL_EVENTS = [
  {
    id: 'belvoir-dog-park',
    title: 'Belvoir Dog Park',
    date: 'Now open',
    description: 'A secure off-lead field beside the Retail Village. Book an hourly slot from £10 and have the field to yourselves. Free parking in the main Castle car park when you book online.',
    image: 'https://BelvoirCastlePull.b-cdn.net/Website/Dog%20Park%201.jpeg',
    location: 'Retail Village',
    link: '/dog-park',
    linkText: 'Visit Dog Park',
    price: '£10+£2 per additional dog',
  },
  {
    id: 'belvoir-wellness',
    title: 'Belvoir Wellness',
    date: 'Coming soon',
    description: 'A new wellness studio at Belvoir Castle. Register your interest and be first to hear when classes and sessions open.',
    image: 'https://BelvoirCastle.b-cdn.net/Wellness/Wellness%203%20(1).webp',
    location: 'Retail Village',
    link: 'https://www.belvoirwellness.com/',
    linkText: 'Visit Belvoir Wellness',
  },
  {
    id: 'belvoir-halloween-crafts-2026',
    title: 'Halloween Crafts',
    date: 'October School Holidays 2026',
    description: 'Hands-on Halloween crafts in the Retail Village. £4.50 per child. Book standalone or add to your Enchanted Halloween Trail visit. Take the magic home.',
    image: 'https://BelvoirCastle.b-cdn.net/Halloween/crafts.webp',
    location: 'Retail Village',
    link: '/halloween-crafts',
    linkText: 'View Details',
    price: '£4.50 per child',
  },
  {
    id: 'santas-grotto-retail-village-2026',
    title: "Santa's Grotto — Retail Village",
    date: 'Saturday, November 21, 2026 to Thursday, December 24, 2026',
    description: 'Meet Santa in his grotto at Belvoir Retail Village. A heartwarming family visit with a present for every child — no Castle entry ticket required.',
    image: 'https://BelvoirCastle.b-cdn.net/Christmas/Santa%20Belvoir%20Retail%20Village%20-%20Image%201.webp',
    location: 'Retail Village',
    link: '/christmas/santas-grotto-retail-village-options',
    linkText: 'View Details',
    price: '£15',
  },
];

const C = {
  navy: '#101d40',
  midnight: '#0A1428',
  gold: '#C9963C',
  goldDk: '#A67C2A',
  ivory: '#FAF7F0',
  parchment: '#F3EDD8',
};

const Page = styled.div`
  min-height: 100vh;
  background: ${C.ivory};
  padding-top: 158px;

  @media (max-width: 768px) {
    padding-top: clamp(188px, 34vw, 228px);
  }
`;

const HeroWrap = styled.section`
  position: relative;
  width: 100%;
  height: clamp(560px, 78vh, 820px);
  overflow: hidden;
  display: flex;
  align-items: flex-end;

  @media (max-width: 768px) {
    height: auto;
    min-height: clamp(560px, 82svh, 720px);
  }
`;

const HeroImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 40%;
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(10, 20, 40, 0.94) 0%,
    rgba(16, 29, 64, 0.55) 42%,
    rgba(16, 29, 64, 0.2) 68%,
    transparent 100%
  );
`;

const HeroPattern = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.16;
  background-image:
    repeating-linear-gradient(60deg, transparent, transparent 38px, rgba(201, 150, 60, 0.55) 38px, rgba(201, 150, 60, 0.55) 40px),
    repeating-linear-gradient(-60deg, transparent, transparent 38px, rgba(16, 29, 64, 0.35) 38px, rgba(16, 29, 64, 0.35) 40px);
  pointer-events: none;
  mask-image: linear-gradient(to top, black 0%, transparent 45%);
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: clamp(1.75rem, 4.5vw, 3rem) clamp(1.25rem, 5vw, 3.5rem);
  color: #fff;
`;

const HeroKicker = styled.p`
  margin: 0 0 0.6rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${C.gold};
  text-shadow: 0 1px 10px rgba(10, 20, 40, 0.45);
`;

const HeroTitle = styled.h1`
  margin: 0 0 0.55rem;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(2.4rem, 5.8vw, 4.2rem);
  font-weight: 700;
  line-height: 1.02;
  color: #fff;
  text-shadow: 0 1px 10px rgba(10, 20, 40, 0.45);

  em {
    font-style: italic;
    color: ${C.gold};
    text-shadow: 0 1px 3px rgba(10, 20, 40, 0.75), 0 4px 16px rgba(10, 20, 40, 0.7);
  }
`;

const HeroStrapline = styled.p`
  margin: 0 0 1.15rem;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(1.05rem, 2.2vw, 1.35rem);
  font-style: italic;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.92);
  max-width: 36rem;
  text-shadow: 0 1px 10px rgba(10, 20, 40, 0.45);
`;

const HeroBadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
  margin-bottom: 1.15rem;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.35rem 0.9rem;
  border-radius: 999px;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: ${p => (p.$fill ? C.gold : 'rgba(16, 29, 64, 0.55)')};
  color: ${p => (p.$fill ? C.navy : '#fff')};
  border: 1px solid ${p => (p.$fill ? 'transparent' : 'rgba(255, 255, 255, 0.35)')};
  backdrop-filter: blur(10px);
`;

const HeroButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;

  @media (max-width: 768px) {
    flex-direction: column;

    button {
      width: 100%;
    }
  }
`;

const BtnPrimary = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.9rem 1.75rem;
  background: ${C.gold};
  color: ${C.navy};
  border: none;
  border-radius: 6px;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease;

  &:hover {
    background: ${C.goldDk};
    color: #fff;
    transform: translateY(-1px);
  }
`;

const BtnGhost = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.9rem 1.75rem;
  background: transparent;
  color: #fff;
  border: 1.5px solid rgba(255, 255, 255, 0.55);
  border-radius: 6px;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;

  &:hover {
    border-color: #fff;
    background: rgba(255, 255, 255, 0.08);
  }
`;

const Section = styled.section`
  padding: clamp(3.5rem, 7vw, 5.5rem) clamp(1.25rem, 5vw, 3.5rem);
  padding-top: ${p => (p.$tightTop ? 'clamp(1.25rem, 2.5vw, 1.75rem)' : 'clamp(3.5rem, 7vw, 5.5rem)')};
  padding-bottom: ${p => (p.$tightBottom ? 'clamp(1.25rem, 2.5vw, 1.75rem)' : 'clamp(3.5rem, 7vw, 5.5rem)')};
  background: ${p => p.$bg || 'transparent'};
  border-bottom: ${p => (p.$border ? '1px solid rgba(16, 29, 64, 0.07)' : 'none')};
`;

const Inner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
`;

const Kicker = styled.p`
  margin: 0 0 0.65rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${p => p.$color || C.gold};
`;

const Divider = styled.div`
  width: 2.5rem;
  height: 2px;
  background: ${C.gold};
  margin-bottom: 1.1rem;
`;

const SectionTitle = styled.h2`
  margin: 0 0 0.75rem;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(1.85rem, 4.5vw, 3rem);
  font-weight: 700;
  line-height: 1.08;
  color: ${p => (p.$light ? '#fff' : C.navy)};

  em {
    font-style: italic;
    color: ${C.gold};
  }
`;

const SectionIntro = styled.p`
  margin: 0 0 1.25rem;
  max-width: 38rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: clamp(0.95rem, 1.7vw, 1.05rem);
  line-height: 1.65;
  color: ${p => (p.$light ? 'rgba(255,255,255,0.78)' : 'rgba(16, 29, 64, 0.68)')};
`;

const TwoCols = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: clamp(1.75rem, 4vw, 3.5rem);
  align-items: center;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1px;
  background: rgba(16, 29, 64, 0.08);
  border-radius: 12px;
  overflow: hidden;
  margin-top: clamp(1.5rem, 3vw, 2.5rem);

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background: #fff;
  padding: clamp(1.4rem, 2.8vw, 2rem);
`;

const FeatureTitle = styled.h3`
  margin: 0 0 0.45rem;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 1.35rem;
  font-weight: 700;
  color: ${C.navy};
`;

const FeatureBody = styled.p`
  margin: 0;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.9rem;
  line-height: 1.55;
  color: rgba(16, 29, 64, 0.62);
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 190px 190px;
  gap: 0.55rem;

  @media (max-width: 800px) {
    grid-template-rows: 160px 160px;
  }
`;

const GalleryItem = styled.div`
  border-radius: 10px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center 28%;
    display: block;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1px;
  background: rgba(16, 29, 64, 0.08);
  border-radius: 10px;
  overflow: hidden;
`;

const InfoItem = styled.div`
  background: #fff;
  padding: clamp(1.25rem, 2.5vw, 1.75rem);
`;

const InfoLabel = styled.div`
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.64rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(16, 29, 64, 0.4);
  margin-bottom: 0.35rem;
`;

const InfoValue = styled.div`
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 1.15rem;
  font-weight: 700;
  color: ${C.navy};
  line-height: 1.25;
`;

const InfoAction = styled.button`
  background: ${C.gold};
  color: ${C.navy};
  border: none;
  padding: clamp(1.25rem, 2.5vw, 1.75rem);
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;

  &:hover {
    background: ${C.goldDk};
    color: #fff;
  }
`;


const CtaSection = styled.section`
  background: ${C.midnight};
  padding: clamp(4rem, 8vw, 6rem) clamp(1.25rem, 5vw, 3.5rem);
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      repeating-linear-gradient(60deg, transparent, transparent 38px, rgba(201, 150, 60, 0.08) 38px, rgba(201, 150, 60, 0.08) 40px),
      repeating-linear-gradient(-60deg, transparent, transparent 38px, rgba(16, 29, 64, 0.2) 38px, rgba(16, 29, 64, 0.2) 40px);
    pointer-events: none;
  }
`;

const CtaInner = styled.div`
  position: relative;
  z-index: 1;
  max-width: 640px;
  margin: 0 auto;
`;

const CtaTitle = styled.h2`
  margin: 0 0 1rem;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(2rem, 5vw, 3.2rem);
  font-weight: 700;
  color: #fff;
  line-height: 1.08;

  em {
    font-style: italic;
    color: ${C.gold};
  }
`;

const CtaBody = styled.p`
  margin: 0 0 2rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: clamp(0.95rem, 1.8vw, 1.05rem);
  color: rgba(255, 255, 255, 0.72);
  line-height: 1.6;
`;

const CtaMeta = styled.p`
  margin: 1.5rem 0 0;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.72rem;
  letter-spacing: 0.06em;
  color: rgba(255, 255, 255, 0.45);
`;

const MeetSantasReindeer = () => {
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>Meet Santa&apos;s Reindeer at Belvoir Retail Village | 19 December 2026</title>
        <meta
          name="description"
          content="Meet two of Santa's reindeer at Belvoir Retail Village on Saturday 19 December 2026, 10am–2pm. No tickets required."
        />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large" />
        <link rel="canonical" href={getCanonicalUrl('events/meet-santas-reindeer')} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Belvoir Castle" />
        <meta property="og:title" content="Meet Santa's Reindeer at Belvoir Retail Village" />
        <meta
          property="og:description"
          content="Saturday 19 December 2026, 10am–2pm at Belvoir Retail Village. No tickets required."
        />
        <meta property="og:url" content={getCanonicalUrl('events/meet-santas-reindeer')} />
        <meta property="og:image" content={HERO_IMG} />
        <meta property="og:locale" content="en_GB" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Event',
            name: "Meet Santa's Reindeer",
            description:
              "Meet two of Santa's reindeer at Belvoir Retail Village. No tickets are required.",
            image: GALLERY.map(photo => photo.src),
            url: getCanonicalUrl('events/meet-santas-reindeer'),
            startDate: '2026-12-19T10:00:00',
            endDate: '2026-12-19T14:00:00',
            eventStatus: 'https://schema.org/EventScheduled',
            eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
            isAccessibleForFree: true,
            location: {
              '@type': 'Place',
              name: 'Belvoir Retail Village',
              address: {
                '@type': 'PostalAddress',
                streetAddress: 'Belvoir Castle',
                addressLocality: 'Grantham',
                postalCode: 'NG32 1PE',
                addressRegion: 'Leicestershire',
                addressCountry: 'GB',
              },
            },
            organizer: {
              '@type': 'Organization',
              name: 'Belvoir Castle',
              url: 'https://www.belvoircastle.com',
            },
          })}
        </script>
      </Helmet>

      <Page>
        <HeroWrap>
          <HeroImg src={HERO_IMG} alt="Santa's reindeer at Belvoir Retail Village" />
          <HeroOverlay />
          <HeroPattern />
          <HeroContent>
            <HeroKicker>Retail Village · Saturday 19 December 2026</HeroKicker>
            <HeroTitle>
              Meet Santa&apos;s <em>Reindeer</em>
            </HeroTitle>
            <HeroStrapline>
              Step into the magic of Christmas at Belvoir Retail Village and meet two of Santa&apos;s very own reindeer.
            </HeroStrapline>
            <HeroBadgeRow>
              <Badge $fill>No tickets required</Badge>
              <Badge>10am – 2pm</Badge>
              <Badge>Retail Village</Badge>
            </HeroBadgeRow>
            <HeroButtons>
              <BtnPrimary type="button" onClick={() => navigate('/retail-village')}>
                Plan your visit
              </BtnPrimary>
              <BtnGhost
                type="button"
                onClick={() =>
                  document.getElementById('reindeer-about')?.scrollIntoView({ behavior: 'smooth' })
                }
              >
                Discover the reindeer
              </BtnGhost>
            </HeroButtons>
          </HeroContent>
        </HeroWrap>

        <Section $bg={C.ivory} $border $tightBottom id="reindeer-about">
          <Inner>
            <TwoCols>
              <div>
                <Kicker>Christmas at the Retail Village</Kicker>
                <Divider />
                <SectionTitle>
                  Two of Santa&apos;s reindeer.
                  <br />
                  <em>A special stop at Belvoir.</em>
                </SectionTitle>
                <SectionIntro>
                  These gentle, enchanting creatures are making a special stop at Belvoir, ready to spread festive cheer and pose for photos with visitors of all ages.
                </SectionIntro>
                <SectionIntro style={{ marginBottom: 0 }}>
                  Bring the whole family to say hello, learn fun facts about them, and soak up the Christmas spirit as you explore the decorated village, with seasonal treats and gifts.
                </SectionIntro>
              </div>
              <GalleryGrid>
                {GALLERY.map(photo => (
                  <GalleryItem key={photo.src}>
                    <img src={photo.src} alt={photo.alt} />
                  </GalleryItem>
                ))}
              </GalleryGrid>
            </TwoCols>

            <FeatureGrid>
              <FeatureCard>
                <FeatureTitle>Meet the reindeer</FeatureTitle>
                <FeatureBody>
                  Two of Santa&apos;s very own reindeer, ready to pose for photos and share a moment of Christmas wonder.
                </FeatureBody>
              </FeatureCard>
              <FeatureCard>
                <FeatureTitle>No tickets required</FeatureTitle>
                <FeatureBody>
                  No tickets are required to meet Belvoir&apos;s newest residents. Just come along on the day.
                </FeatureBody>
              </FeatureCard>
              <FeatureCard>
                <FeatureTitle>For the whole family</FeatureTitle>
                <FeatureBody>
                  A heart-warming outing for children and grown-ups alike, in the decorated Retail Village.
                </FeatureBody>
              </FeatureCard>
            </FeatureGrid>
          </Inner>
        </Section>

        <Section $bg={C.parchment} $border $tightTop>
          <Inner>
            <Kicker>Plan Your Visit</Kicker>
            <Divider />
            <SectionTitle>
              Essential <em>Information</em>
            </SectionTitle>
            <InfoGrid>
              <InfoItem>
                <InfoLabel>When</InfoLabel>
                <InfoValue>
                  Saturday
                  <br />
                  19 December 2026
                </InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Time</InfoLabel>
                <InfoValue>
                  10am – 2pm
                </InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Where</InfoLabel>
                <InfoValue>
                  Belvoir
                  <br />
                  Retail Village
                </InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Tickets</InfoLabel>
                <InfoValue>
                  No tickets
                  <br />
                  required
                </InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Location</InfoLabel>
                <InfoValue>
                  Belvoir Castle
                  <br />
                  NG32 1PE
                </InfoValue>
              </InfoItem>
              <InfoAction type="button" onClick={() => navigate('/retail-village')}>
                Plan your visit
              </InfoAction>
            </InfoGrid>
          </Inner>
        </Section>

        <WhatsOn
          title="Also at the Retail Village"
          events={OTHER_RETAIL_EVENTS}
          wheelScroll
          showParklandSection={false}
        />

        <CtaSection>
          <CtaInner>
            <CtaTitle>
              A moment of Christmas wonder
              <br />
              <em>you won&apos;t want to miss.</em>
            </CtaTitle>
            <CtaBody>
              Meet Santa&apos;s reindeer at Belvoir Retail Village on Saturday 19 December 2026, from 10am to 2pm. No tickets are required.
            </CtaBody>
            <BtnPrimary type="button" onClick={() => navigate('/retail-village')}>
              Explore the Retail Village
            </BtnPrimary>
            <CtaMeta>
              Saturday 19 December 2026 &nbsp;·&nbsp; 10am – 2pm &nbsp;·&nbsp; Retail Village
            </CtaMeta>
          </CtaInner>
        </CtaSection>

        <Footer />
      </Page>
    </>
  );
};

export default MeetSantasReindeer;
